import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { displaySpecLabel, localizedRecordLabel } from '@/i18n/catalogDisplayText';
import { useLocation } from 'react-router-dom';
import { Plus } from 'lucide-react';
import SheinOpsPager, { SHEIN_OPS_DEFAULT_PAGE_SIZE } from '@/shein-platform/components/SheinOpsPager';
import { togglePageSelection } from '@/shein-platform/utils/sheinPageSelection';
import HoverCopyField from '@/shein-platform/modules/products/components/HoverCopyField';
import SheinProductThumb from '@/shein-platform/modules/products/components/SheinProductThumb';
import { ProductFilterCard, ProductListCard } from '@/shein-platform/modules/products/components/ProductPageCards';
import { downloadCsv } from '@/shein-platform/modules/products/components/SheinPageToolbar';
import SheinOpsListToolbar from '@/shein-platform/components/SheinOpsListToolbar';
import SheinSyncProgressSlot from '@/shein-platform/components/SheinSyncProgressSlot';
import BatchQueryModal from '@/shein-platform/modules/products/components/BatchQueryModal';
import {
  BATCH_QUERY_LIMIT,
  joinBatchTokens,
  parseBatchTokens,
} from '@/shein-platform/modules/products/components/batchQueryUtils';
import { openSheinPathInNewTab } from '@/shein-platform/constants/sheinRoutes';
import { useSheinPageShopScope, stackShopDimensionTabs } from '@/shein-platform/hooks/useSheinPageShopScope';
import { useSheinSyncJobs } from '@/shein-platform/context/SheinSyncJobContext';
import { useGuardedRefresh } from '@/shein-platform/hooks/useGuardedRefresh';
import { showSheinConfirm } from '@/shein-platform/components/SheinConfirmDialog';
import { formatSiteListSyncedAt } from '@/shein-platform/utils/sheinSiteList';
import { checkSheinProductEditable } from '@/shein-platform/utils/checkSheinProductEditable';
import { fetchAllPages } from '@/shein-platform/utils/sheinExportHelpers';
import { buildOpsExportMenu, opsExportLabel } from '@/shein-platform/utils/buildOpsExportMenu';
import { sheinPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';
import { formatBeijingDateTime } from '@/shein-platform/utils/formatBeijingDateTime';

const PUBLISH_RECORD_EXPORT_HEADERS = [
  'id', 'title', 'supplierCode', 'platformSpuName', 'status', 'remark', 'action', 'source',
  'createdAt', 'publishedAt', 'finishedAt', 'listedAt', 'hopeOnSaleDate',
];

const PUBLISH_RECORDS_SYNCED_AT_KEY = 'ipe.shein.publishRecords.lastSyncedAt';

function publishRecordsSyncedAtStorageKey(shopId) {
  return `${PUBLISH_RECORDS_SYNCED_AT_KEY}.${shopId || ''}`;
}

function readStoredPublishRecordsSyncedAt(shopId) {
  if (!shopId || typeof localStorage === 'undefined') return '';
  try {
    return String(localStorage.getItem(publishRecordsSyncedAtStorageKey(shopId)) || '').trim();
  } catch {
    return '';
  }
}

function writeStoredPublishRecordsSyncedAt(shopId, iso) {
  if (!shopId || typeof localStorage === 'undefined') return;
  const value = String(iso || '').trim();
  try {
    if (!value) {
      localStorage.removeItem(publishRecordsSyncedAtStorageKey(shopId));
      return;
    }
    localStorage.setItem(publishRecordsSyncedAtStorageKey(shopId), value);
  } catch {
    /* ignore quota */
  }
}

function pickLatestIso(...values) {
  let best = '';
  let bestTs = Number.NEGATIVE_INFINITY;
  values.forEach((raw) => {
    const text = String(raw || '').trim();
    if (!text) return;
    const ts = Date.parse(text);
    if (!Number.isFinite(ts)) return;
    if (ts >= bestTs) {
      bestTs = ts;
      best = text;
    }
  });
  return best;
}

function maxPlatformSyncedAtFromItems(items = []) {
  let best = '';
  (Array.isArray(items) ? items : []).forEach((row) => {
    best = pickLatestIso(best, row?.platformSyncedAt, row?.finishedAt, row?.updatedAt);
  });
  return best;
}

const ID_TYPE_OPTIONS = [
  { value: 'skc', label: 'SKC' },
  { value: 'spu', label: 'SPU' },
  { value: 'sku', label: 'SKU' },
];

const EMPTY_FILTERS = {
  idType: 'skc',
  idValue: '',
  title: '',
  supplierCode: '',
};

const EMPTY_QUERY = {
  status: '',
  idType: 'skc',
  keywords: '',
  title: '',
  supplierCode: '',
};

const BATCH_FIELDS = {
  idValue: {
    titleKey: 'idType',
    titleMap: { skc: 'publishRecord.batchSKC', spu: 'publishRecord.batchSPU', sku: 'publishRecord.batchSKU' },
    placeholder: 'publishRecord.batchCodePh',
  },
  title: {
    title: 'publishRecord.batchTitle',
    placeholder: 'publishRecord.batchTitlePh',
  },
  supplierCode: {
    title: 'publishRecord.batchSupplier',
    placeholder: 'publishRecord.batchSupplierPh',
  },
};

const STATUS_LABEL = {
  success: '已通过',
  failed: '失败',
  pending: '处理中',
  处理中: '处理中',
  审核中: '待审核',
  待审核: '待审核',
  待商家确认: '待商家确认',
  接受失败: '接受失败',
  审批成功: '已通过',
  审批失败: '已驳回',
  待审款: '待审核',
  待核价: '待审核',
  待核样: '待审核',
  待终审: '待审核',
  已驳回: '已驳回',
  申诉中: '申诉中',
  已通过: '已通过',
  已撤回: '已撤回',
  已归档: '已撤回',
  已上架: '已通过',
  已下架: '已通过',
  审核驳回: '已驳回',
  // query-document-state numeric codes
  '-1': '接受失败',
  '0': '处理中',
  '1': '待审核',
  '2': '已通过',
  '3': '已驳回',
  '4': '已撤回',
  '5': '申诉中',
};

/** 表头状态维度（官方 documentState + 议价待确认派生） */
const STATUS_TABS = [
  { id: 'all', label: '全部', status: '' },
  { id: '处理中', label: '处理中', status: '处理中' },
  { id: '接受失败', label: '接受失败', status: '接受失败' },
  { id: '待商家确认', label: '待商家确认', status: '待商家确认' },
  { id: '待审核', label: '待审核', status: '待审核' },
  { id: '已通过', label: '已通过', status: '已通过' },
  { id: '已驳回', label: '已驳回', status: '已驳回' },
  { id: '已撤回', label: '已撤回', status: '已撤回' },
  { id: '申诉中', label: '申诉中', status: '申诉中' },
];

function statusTabCount(stats, tab) {
  if (!stats) return 0;
  if (tab.id === 'all') return Number(stats.all) || 0;
  return Number(stats[tab.status]) || 0;
}

function resolveStatusLabel(item) {
  return mapDocumentStateLabel(item?.statusLabel ?? item?.status ?? 'pending');
}

function mapDocumentStateLabel(raw) {
  const status = String(raw ?? '').trim();
  if (!status || status === 'pending') return '处理中';
  if (STATUS_LABEL[status]) return STATUS_LABEL[status];
  return status;
}

function resolveRejectRemark(skc, record) {
  const skcRemark = String(skc?.remark || skc?.rejectRemark || '').trim();
  if (skcRemark) return skcRemark;
  return String(record?.remark || record?.rejectRemark || '').trim();
}

function resolveSkcStatusLabel(skc, record) {
  if (Number(skc?.discussStatus) === 1) {
    return '待商家确认';
  }
  // 优先用已是中文的 statusLabel；纯数字码一律走 documentState 映射，避免直接展示 "0"
  const label = skc?.statusLabel != null ? String(skc.statusLabel).trim() : '';
  if (label && !/^-?\d+$/.test(label)) {
    return STATUS_LABEL[label] || label;
  }
  return mapDocumentStateLabel(
    skc?.status ?? skc?.statusLabel ?? record?.statusLabel ?? record?.status ?? 'pending'
  );
}

function resolveStatusTone(status) {
  const s = mapDocumentStateLabel(status);
  if (s === '接受失败' || s.includes('驳回') || s === '失败') return 'failed';
  if (s === '已通过') return 'success';
  if (s === '已撤回') return 'offline';
  if (s === '申诉中') return 'appeal';
  if (s === '待商家确认') return 'pending';
  if (s === '待审核' || s === '处理中') return 'pending';
  return 'pending';
}

/** 仅「待审核」展示并可调用平台撤回接口（议价待确认需先去议价单处理） */
function canRevokeRecord(item) {
  const label = resolveStatusLabel(item);
  if (label === '待商家确认') return false;
  if (label === '待审核') return true;
  const skcList = Array.isArray(item?.skcItems) ? item.skcItems : [];
  return skcList.some((skc) => resolveSkcStatusLabel(skc, item) === '待审核');
}

function needsRepullBeforeRevoke(item) {
  const label = resolveStatusLabel(item);
  return label === '处理中' || label === '接受失败';
}

function canDeleteRecord(item) {
  // 暂不限制状态，便于清理历史脏数据（后端本地软删）
  if (item?.canDelete === false) return false;
  return Boolean(item?.id);
}

function RecordSkcThumb({ imageUrl }) {
  return <SheinProductThumb imageUrl={imageUrl} />;
}

/** 发布记录：布局对齐草稿箱，并增加状态列 */
export default function ProductPublishRecordsPage() {
  const { t, i18n } = useTranslation('workbench');
  const {
    activeShop,
    activeShopId,
    concreteScopeId,
    allShopsScope,
    shopDimensionTabs,
  } = useSheinPageShopScope();
  const shopId = concreteScopeId || (allShopsScope ? '' : (activeShop?.id || activeShopId));
  const location = useLocation();

  const [filterDraft, setFilterDraft] = useState(EMPTY_FILTERS);
  const [query, setQuery] = useState(EMPTY_QUERY);
  const [statusDimension, setStatusDimension] = useState('all');
  const [statusCounts, setStatusCounts] = useState(null);
  const [queryNonce, setQueryNonce] = useState(0);
  const [batchModalField, setBatchModalField] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(SHEIN_OPS_DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [copyingId, setCopyingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [lastSyncedAt, setLastSyncedAt] = useState(() => readStoredPublishRecordsSyncedAt(''));
  const [toolbarBusy, setToolbarBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const selectedRowsRef = useRef(new Map());
  const { startJob, onTrackDone, isBusy } = useSheinSyncJobs();

  useEffect(() => {
    const map = selectedRowsRef.current;
    for (const id of [...map.keys()]) {
      if (!selectedIds.has(id)) map.delete(id);
    }
    items.forEach((row) => {
      if (row?.id != null && selectedIds.has(row.id)) map.set(row.id, row);
    });
  }, [items, selectedIds]);

  // 发品成功跳转进来：强制重新拉列表；再补一次延迟刷新（后端可能刚补全本地缩略图）
  useEffect(() => {
    if (!location.state?.refreshPublishRecords) return undefined;
    setPage(1);
    setQueryNonce((n) => n + 1);
    const t = window.setTimeout(() => setQueryNonce((n) => n + 1), 1500);
    return () => window.clearTimeout(t);
  }, [location.state?.refreshPublishRecords, location.state?.at]);

  const loadRecords = useCallback(async () => {
    if (!shopId) {
      setItems([]);
      setTotal(0);
      setStatusCounts(null);
      setSelectedIds(new Set());
      return;
    }
    setLoading(true);
    try {
      const res = await sheinPlatformAPI.listPublishRecords(shopId, {
        status: query.status || undefined,
        idType: query.keywords ? (query.idType || 'skc') : undefined,
        keywords: query.keywords || undefined,
        title: query.title || undefined,
        supplierCode: query.supplierCode || undefined,
        page,
        pageSize,
      });
      const nextItems = Array.isArray(res?.items) ? res.items : [];
      setItems(nextItems);
      setTotal(Number(res?.total) || 0);
      const counts = res?.statusCounts && typeof res.statusCounts === 'object' ? res.statusCounts : null;
      setStatusCounts(counts);
      // 跨页选中：翻页刷新列表时保留已选 id
      const fromItems = maxPlatformSyncedAtFromItems(nextItems);
      const stored = readStoredPublishRecordsSyncedAt(shopId);
      const nextSynced = pickLatestIso(fromItems, stored);
      if (nextSynced) {
        setLastSyncedAt(nextSynced);
        writeStoredPublishRecordsSyncedAt(shopId, nextSynced);
      }
    } catch (e) {
      setItems([]);
      setTotal(0);
      setStatusCounts(null);
      showToast(e?.message || t('publishRecord.loadFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [shopId, query.status, query.idType, query.keywords, query.title, query.supplierCode, page, pageSize, t]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords, queryNonce]);

  useEffect(() => {
    setFilterDraft(EMPTY_FILTERS);
    setQuery(EMPTY_QUERY);
    setBatchModalField(null);
    setPage(1);
    setQueryNonce((n) => n + 1);
    setSelectedIds(new Set());
    setLastSyncedAt(readStoredPublishRecordsSyncedAt(shopId));
  }, [shopId]);

  const refreshDocumentStatusesAction = useCallback(async () => {
    if (!shopId) {
      showToast(t('publishRecord.pickShop'), 'warning');
      return;
    }
    const track = await startJob({
      kind: 'publishRecord',
      shopId,
      label: t('publishRecord.syncUpdate'),
      silent: true,
    });
    if (!track?.id) return;
    await new Promise((resolve) => {
      const unsub = onTrackDone(track.id, async (result) => {
        unsub();
        if (result?.ok) {
          const latest = result.view || {};
          const refreshed = Number(latest.imported) || 0;
          const finishedAt = latest.finishedAt || new Date().toISOString();
          setLastSyncedAt(String(finishedAt));
          writeStoredPublishRecordsSyncedAt(shopId, finishedAt);
          showToast(
            refreshed > 0
              ? t('publishRecord.synced', { count: refreshed })
              : (latest.message || t('publishRecord.nothingToSyncSpu')),
            refreshed > 0 ? 'success' : 'info',
          );
          await loadRecords();
        }
        resolve();
      });
    });
  }, [shopId, loadRecords, startJob, onTrackDone, t]);

  const {
    refresh: onSyncStatuses,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(refreshDocumentStatusesAction);

  const syncingStatuses = syncingLocal || isBusy('publishRecord', shopId);

  const latestSyncedLabel = lastSyncedAt
    ? t('publishRecord.lastUpdate', { time: formatSiteListSyncedAt(lastSyncedAt) })
    : t('publishRecord.notSynced');

  const selectableIds = useMemo(() => items.map((row) => row.id).filter(Boolean), [items]);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

  const toggleAll = () => {
    setSelectedIds((prev) => togglePageSelection(prev, selectableIds, allSelected));
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const updateField = (key, value) => {
    setFilterDraft((prev) => ({ ...prev, [key]: value }));
  };

  const openBatchModal = (fieldKey) => {
    setBatchModalField(fieldKey);
  };

  const onBatchConfirm = (tokens) => {
    if (!batchModalField) return;
    updateField(batchModalField, joinBatchTokens(tokens));
  };

  const batchModalMeta = batchModalField ? BATCH_FIELDS[batchModalField] : null;
  const batchModalTitle = t(batchModalMeta
    ? (batchModalMeta.title
      || batchModalMeta.titleMap?.[filterDraft[batchModalMeta.titleKey]]
      || 'publishRecord.batchQuery')
    : 'publishRecord.batchQuery');
  const idTypeLabel = ID_TYPE_OPTIONS.find((o) => o.value === filterDraft.idType)?.label || 'SKC';

  const onQuery = () => {
    let idTokens = parseBatchTokens(filterDraft.idValue).slice(0, BATCH_QUERY_LIMIT);
    let titleTokens = parseBatchTokens(filterDraft.title).slice(0, 5);
    let supplierTokens = parseBatchTokens(filterDraft.supplierCode).slice(0, 5);
    const idType = filterDraft.idType || 'skc';
    if (idType !== 'spu' && idTokens.length > 20) {
      idTokens = idTokens.slice(0, 20);
      showToast(t('publishRecord.batchIdCap'), 'info');
    } else if (parseBatchTokens(filterDraft.idValue).length > BATCH_QUERY_LIMIT) {
      showToast(t('publishRecord.batchCodeCap', { limit: BATCH_QUERY_LIMIT }), 'info');
    }
    if (parseBatchTokens(filterDraft.title).length > 5) {
      showToast(t('publishRecord.batchTitleCap'), 'info');
    }
    if (parseBatchTokens(filterDraft.supplierCode).length > 5) {
      showToast(t('publishRecord.batchSupplierCap'), 'info');
    }
    if (shopId) setLoading(true);
    setPage(1);
    setQuery({
      status: statusDimension === 'all' ? '' : statusDimension,
      idType,
      keywords: idTokens.join(','),
      title: titleTokens.join(','),
      supplierCode: supplierTokens.join(','),
    });
    setQueryNonce((n) => n + 1);
  };

  const onStatusDimensionChange = (nextId) => {
    if (!nextId || nextId === statusDimension) return;
    setStatusDimension(nextId);
    setPage(1);
    setSelectedIds(new Set());
    if (shopId) setLoading(true);
    setQuery((prev) => ({
      ...prev,
      status: nextId === 'all' ? '' : nextId,
    }));
    setQueryNonce((n) => n + 1);
  };

  const statusTabs = useMemo(() => (
    <div className="spo-status-tabs" role="tablist" aria-label={t('publishRecord.tabsAria')}>
      {STATUS_TABS.map((tab) => {
        const active = statusDimension === tab.id;
        const count = statusTabCount(statusCounts, tab);
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            className={`spo-status-tab${active ? ' spo-status-tab--active' : ''}`}
            onClick={() => onStatusDimensionChange(tab.id)}
          >
            {t(`publishRecord.${({
              all: 'tabAll',
              '待审核': 'tabPendingReview',
              '已通过': 'tabApproved',
              '已上架': 'tabListed',
              '已驳回': 'tabRejected',
              '待商家确认': 'tabMerchant',
              '处理中': 'tabProcessing',
              '接受失败': 'tabAcceptFailed',
              '已撤回': 'tabWithdrawn',
              '申诉中': 'tabAppeal',
            })[tab.id] || 'tabAll'}`, { defaultValue: tab.label })}
            <span className="spo-status-tab__count">{count}</span>
          </button>
        );
      })}
    </div>
  ), [statusDimension, statusCounts, t]);

  const onReset = () => {
    setFilterDraft(EMPTY_FILTERS);
    setStatusDimension('all');
    setQuery(EMPTY_QUERY);
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const onCopyToDraft = async (item) => {
    if (!shopId) {
      showToast(t('publishRecord.pickShop'), 'warning');
      return;
    }
    if (!item?.id) {
      showToast(t('publishRecord.missingRecord'), 'warning');
      return;
    }
    if (copyingId) return;
    setCopyingId(item.id);
    try {
      const res = await sheinPlatformAPI.copyPublishRecordToDraft(shopId, item.id, {
        spuName: item.platformSpuName || '',
      });
      const draftId = res?.id;
      showToast(t('publishRecord.copied'), 'success');
      if (draftId) {
        openSheinPathInNewTab(`products/publish?draftId=${encodeURIComponent(draftId)}`);
      } else {
        openSheinPathInNewTab('products/drafts');
      }
    } catch (e) {
      showToast(e?.message || t('publishRecord.copyFailed'), 'error');
    } finally {
      setCopyingId(null);
    }
  };

  const onEdit = async (item) => {
    // 有平台 SPU：先校验是否可编辑，再打开编辑页
    if (item?.platformSpuName) {
      if (!shopId) {
        showToast(t('publishRecord.pickShop'), 'warning');
        return;
      }
      setEditingId(item.id);
      try {
        const check = await checkSheinProductEditable(shopId, item.platformSpuName);
        if (!check.ok) {
          showToast(check.reason || t('publishRecord.notEditable'), 'warning');
          return;
        }
        const qs = new URLSearchParams();
        if (item.id != null) qs.set('recordId', String(item.id));
        if (item.draftId != null) qs.set('draftId', String(item.draftId));
        const q = qs.toString();
        openSheinPathInNewTab(
          `products/edit/${encodeURIComponent(item.platformSpuName)}${q ? `?${q}` : ''}`,
        );
      } finally {
        setEditingId(null);
      }
      return;
    }
    // 无 SPU（常见于历史「假成功」记录）：回填关联草稿表单；发品后草稿会软删，后端仍允许按 ID 读取
    if (item?.draftId) {
      openSheinPathInNewTab(`products/publish?draftId=${encodeURIComponent(item.draftId)}`);
      return;
    }
    showToast(t('publishRecord.cannotEditRefresh'), 'warning');
  };

  const onRevoke = async (item) => {
    if (!shopId || !item?.id) return;
    const label = resolveStatusLabel(item);
    if (needsRepullBeforeRevoke(item)) {
      showToast(t('publishRecord.withdrawNeedSync', { label, platform: 'SHEIN' }), 'warning');
      return;
    }
    if (!canRevokeRecord(item)) {
      showToast(t('publishRecord.withdrawBlocked', { label }), 'warning');
      return;
    }
    const confirmed = await showSheinConfirm({
      title: t('publishRecord.confirmWithdraw'),
      message: t('publishRecord.withdrawOne'),
      tone: 'warning',
      confirmText: t('publishRecord.withdraw'),
      cancelText: t('shop.cancel'),
    });
    if (!confirmed) return;
    setRevokingId(item.id);
    try {
      await sheinPlatformAPI.revokePublishRecord(shopId, item.id);
      showToast(t('publishRecord.withdrawn'), 'success');
      loadRecords();
    } catch (e) {
      showToast(e?.message || t('publishRecord.withdrawFailed'), 'error');
    } finally {
      setRevokingId(null);
    }
  };

  const writePublishRecordsCsv = useCallback((rows, filename) => {
    downloadCsv(
      filename,
      PUBLISH_RECORD_EXPORT_HEADERS,
      rows.map((row) => ({
        id: row.id,
        title: row.title,
        supplierCode: row.supplierCode,
        platformSpuName: row.platformSpuName,
        status: resolveStatusLabel(row),
        remark: Array.isArray(row?.skcItems)
          ? [...new Set((row.skcItems || []).map((skc) => resolveRejectRemark(skc, row)).filter(Boolean))].join('；')
          : resolveRejectRemark(null, row),
        action: row.action,
        source: row.source,
        createdAt: row.createdAt,
        publishedAt: row.publishedAt || row.finishedAt,
        finishedAt: row.finishedAt,
        listedAt: row.listedAt || '',
        hopeOnSaleDate: row.hopeOnSaleDate || row.shelfLabel || '',
      })),
    );
  }, []);

  const fetchAllPublishRecords = useCallback(async () => {
    if (!shopId) return [];
    return fetchAllPages(async (pageNo, size) => {
      const res = await sheinPlatformAPI.listPublishRecords(shopId, {
        status: query.status || undefined,
        idType: query.keywords ? (query.idType || 'skc') : undefined,
        keywords: query.keywords || undefined,
        title: query.title || undefined,
        supplierCode: query.supplierCode || undefined,
        page: pageNo,
        pageSize: size,
      });
      return {
        items: Array.isArray(res?.items) ? res.items : [],
        total: Number(res?.total) || 0,
      };
    });
  }, [shopId, query.status, query.idType, query.keywords, query.title, query.supplierCode]);

  const exportFilteredRecords = useCallback(async () => {
    if (!shopId) {
      showToast(t('publishRecord.pickShop'), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const rows = await fetchAllPublishRecords();
      if (!rows.length) {
        showToast(t('publishRecord.noneToExport'), 'warning');
        return;
      }
      writePublishRecordsCsv(rows, `shein-publish-records-filtered-${Date.now()}.csv`);
      showToast(t('publishRecord.exportedFiltered', { count: rows.length }), 'success');
    } catch (e) {
      showToast(e?.message || t('publishRecord.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  }, [shopId, exporting, fetchAllPublishRecords, writePublishRecordsCsv, t]);

  const exportPageRecords = useCallback(() => {
    if (!items.length) {
      showToast(t('publishRecord.noneToExport'), 'warning');
      return;
    }
    writePublishRecordsCsv(items, `shein-publish-records-page-${Date.now()}.csv`);
    showToast(t('publishRecord.exportedPage', { count: items.length }), 'success');
  }, [items, writePublishRecordsCsv, t]);

  const exportSelectedRecords = useCallback(() => {
    const rows = Array.from(selectedIds)
      .map((id) => selectedRowsRef.current.get(id))
      .filter(Boolean);
    if (!rows.length) {
      showToast(t('publishRecord.pickToExport'), 'warning');
      return;
    }
    writePublishRecordsCsv(rows, `shein-publish-records-selected-${Date.now()}.csv`);
    showToast(t('publishRecord.exportedSelected', { count: rows.length }), 'success');
  }, [selectedIds, writePublishRecordsCsv, t]);

  const onBatchDelete = async () => {
    if (!shopId || selectedIds.size === 0) {
      showToast(t('publishRecord.pickToDelete'), 'warning');
      return;
    }
    const ids = Array.from(selectedIds)
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('publishRecord.invalidSelection'), 'warning');
      return;
    }
    const confirmed = await showSheinConfirm({
      title: t('publishRecord.batchDelete'),
      message: t('publishRecord.batchDeleteMsg', { count: ids.length, platform: 'SHEIN' }),
      tone: 'danger',
      confirmText: t('publishRecord.delete'),
      cancelText: t('shop.cancel'),
    });
    if (!confirmed) return;
    setToolbarBusy(true);
    try {
      const res = await sheinPlatformAPI.batchDeletePublishRecords(shopId, ids);
      const deleted = Number(res?.deleted);
      const count = Number.isFinite(deleted) && deleted >= 0 ? deleted : ids.length;
      if (count > 0) {
        showToast(t('publishRecord.deletedCount', { count }), 'success');
      } else {
        showToast(t('publishRecord.noneDeleted'), 'warning');
      }
      setSelectedIds(new Set());
      loadRecords();
    } catch (e) {
      showToast(e?.message || t('publishRecord.batchDeleteFailed'), 'error');
    } finally {
      setToolbarBusy(false);
    }
  };

  const onBatchRevoke = async () => {
    if (!shopId || selectedIds.size === 0) {
      showToast(t('publishRecord.pickToWithdraw'), 'warning');
      return;
    }
    const selected = Array.from(selectedIds)
      .map((id) => selectedRowsRef.current.get(id))
      .filter(Boolean);
    const revocable = selected.filter((item) => canRevokeRecord(item));
    const needRepull = selected.filter((item) => needsRepullBeforeRevoke(item));
    if (!revocable.length) {
      if (needRepull.length) {
        showToast(t('publishRecord.withdrawNeedPull', { platform: 'SHEIN' }), 'warning');
      } else {
        showToast(t('publishRecord.withdrawNone'), 'warning');
      }
      return;
    }
    const confirmed = await showSheinConfirm({
      title: t('publishRecord.batchWithdraw'),
      message: `${t('publishRecord.batchWithdrawMsg', { count: revocable.length })}${
        selected.length > revocable.length
          ? t('publishRecord.batchWithdrawSkipped', {
            skipped: selected.length - revocable.length,
            extra: needRepull.length
              ? t('publishRecord.batchWithdrawRepull', { count: needRepull.length })
              : '',
          })
          : ''
      }`,
      tone: 'warning',
      confirmText: t('publishRecord.withdraw'),
      cancelText: t('shop.cancel'),
    });
    if (!confirmed) return;
    setToolbarBusy(true);
    let ok = 0;
    let fail = 0;
    try {
      for (const item of revocable) {
        try {
          await sheinPlatformAPI.revokePublishRecord(shopId, item.id);
          ok += 1;
        } catch {
          fail += 1;
        }
      }
      if (ok > 0 && fail === 0) {
        showToast(t('publishRecord.withdrawnCount', { count: ok }), 'success');
      } else if (ok > 0) {
        showToast(t('publishRecord.withdrawPartial', { ok, fail }), 'warning');
      } else {
        showToast(t('publishRecord.batchWithdrawFailed'), 'error');
      }
      setSelectedIds(new Set());
      loadRecords();
    } finally {
      setToolbarBusy(false);
    }
  };

  const colSpan = 8;

  return (
    <>
      <SheinSyncProgressSlot kinds={['publishRecord']} />
      <ProductFilterCard
        toolbar={(
          <SheinOpsListToolbar
            selectedCount={selectedIds.size}
            busy={toolbarBusy || loading || Boolean(revokingId) || exporting}
            syncKinds={['publishRecord']}
            onSync={() => {
              if (!shopId) {
                showToast(t('publishRecord.pickAuthorized', { platform: 'SHEIN' }), 'warning');
                return;
              }
              onSyncStatuses();
            }}
            syncing={syncingStatuses}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!shopId || syncingStatuses}
            lastSyncedLabel={latestSyncedLabel}
            syncHint={t('publishRecord.syncHintShein')}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: items.length,
              selectedCount: selectedIds.size,
              onExportFiltered: exportFilteredRecords,
              onExportPage: exportPageRecords,
              onExportSelected: exportSelectedRecords,
            })}
            onBatchDelete={onBatchDelete}
            deleteDisabled={!shopId}
            extraActions={[
              {
                key: 'revoke',
                label: t('publishRecord.batchWithdraw'),
                disabled: !shopId || selectedIds.size === 0,
                onClick: onBatchRevoke,
              },
            ]}
          />
        )}
      >
        <div className="spo-filter-bar">
          <div className="spo-filter-bar__fields">
            <div className="spo-field spo-field--inline spo-field--id-combo spo-field--batch">
              <select
                className="spo-select spo-select--compact"
                value={filterDraft.idType}
                disabled={!shopId}
                onChange={(e) => updateField('idType', e.target.value)}
                aria-label={t('publishRecord.idTypeAria')}
              >
                {ID_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <input
                className="spo-input"
                value={filterDraft.idValue}
                placeholder={t('publishRecord.idPlaceholder', { type: idTypeLabel })}
                disabled={!shopId}
                onChange={(e) => updateField('idValue', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="spo-batch-add-btn"
                aria-label={t('publishRecord.batchInputId', { type: idTypeLabel })}
                title={t('publishRecord.batchQuery')}
                disabled={!shopId}
                onClick={() => openBatchModal('idValue')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>

            <div className="spo-field spo-field--inline spo-field--batch">
              <span className="spo-field__label">{t('publishRecord.title')}</span>
              <input
                className="spo-input"
                value={filterDraft.title}
                placeholder={t('publishRecord.titlePlaceholder')}
                disabled={!shopId}
                onChange={(e) => updateField('title', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="spo-batch-add-btn"
                aria-label={t('publishRecord.batchInputTitle')}
                title={t('publishRecord.batchQuery')}
                disabled={!shopId}
                onClick={() => openBatchModal('title')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>

            <div className="spo-field spo-field--inline spo-field--batch">
              <span className="spo-field__label">{t('publishRecord.supplier')}</span>
              <input
                className="spo-input"
                value={filterDraft.supplierCode}
                placeholder={t('publishRecord.supplierPlaceholder')}
                disabled={!shopId}
                onChange={(e) => updateField('supplierCode', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="spo-batch-add-btn"
                aria-label={t('publishRecord.batchInputSupplier')}
                title={t('publishRecord.batchQuery')}
                disabled={!shopId}
                onClick={() => openBatchModal('supplierCode')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="spo-filter-bar__actions">
            <button type="button" className="spo-btn spo-btn--secondary spo-btn--sm" onClick={onReset} disabled={!shopId}>
              {t('publishRecord.reset')}
            </button>
            <button type="button" className="spo-btn spo-btn--primary spo-btn--sm" onClick={onQuery} disabled={!shopId}>
              {t('publishRecord.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <ProductListCard
        tabs={stackShopDimensionTabs(shopDimensionTabs, statusTabs)}
        loading={Boolean(shopId) && loading}
        loadingLabel={t('publishRecord.querying')}
      >
        <div className={`wf-tasks-page platform-product-page wf-tasks-page--embedded platform-product-page--ops-chrome${loading ? ' platform-product-page--loading' : ''}`}>
          <div className="wf-tasks-table-panel">
            <div className="wf-tasks-table-wrap" role="region" aria-live="polite">
              <table className="wf-tasks-table platform-product-table platform-product-table--shein platform-product-table--drafts platform-product-table--records">
                <colgroup>
                  <col className="shein-product-col--check" />
                  <col className="shein-product-col--info" />
                  <col className="shein-product-col--skc" />
                  <col className="shein-product-col--type" />
                  <col className="shein-product-col--status" />
                  <col className="shein-product-col--remark" />
                  <col className="shein-product-col--time" />
                  <col className="shein-product-col--ops" />
                </colgroup>
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="shein-product-cell--spu-check"
                      onClick={() => {
                        if (!shopId || loading || selectableIds.length === 0) return;
                        toggleAll();
                      }}
                    >
                      <input
                        type="checkbox"
                        aria-label={t('publishRecord.selectAll')}
                        disabled={!shopId || loading || selectableIds.length === 0}
                        checked={allSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={toggleAll}
                      />
                    </th>
                    <th scope="col">{t('publishRecord.productInfo')}</th>
                    <th scope="col">SKC</th>
                    <th scope="col">{t('publishRecord.type')}</th>
                    <th scope="col">{t('publishRecord.status')}</th>
                    <th scope="col">{t('publishRecord.remark')}</th>
                    <th scope="col">{t('publishRecord.time')}</th>
                    <th scope="col">{t('publishRecord.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {!shopId || loading || items.length === 0 ? (
                    <tr>
                      <td colSpan={colSpan} className="wf-tasks-table__empty">
                        <div className="funding-empty-state">
                          <p className="funding-empty-state__title">
                            {!shopId ? (allShopsScope ? t('publishRecord.pickConcrete') : t('publishRecord.pickShop')) : loading ? t('publishRecord.loading') : t('publishRecord.empty')}
                          </p>
                          {!shopId || loading ? null : (
                            <p className="funding-empty-state__description">
                              {t('publishRecord.emptyHintShein')}
                            </p>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    items.flatMap((item) => {
                      const skcItems = Array.isArray(item.skcItems) && item.skcItems.length
                        ? item.skcItems
                        : [{ colorLabel: null, skcName: null, imageUrl: null, merchantSkus: [] }];
                      const typeLabel = item.typeLabel
                        || (item.source === 'sync' ? t('publishRecord.sourceSync') : (item.action === 'edit' ? t('publishRecord.sourceEdit') : t('publishRecord.sourceNew')));
                      const rowSpan = Math.max(1, skcItems.length);
                      const createdAt = item.createdAt;
                      const publishedAt = item.publishedAt || item.finishedAt;
                      const shelfLabel = item.shelfLabel
                        || item.hopeOnSaleDate
                        || (item.shelfWay === '2' ? '—' : t('publishRecord.autoList'));

                      return skcItems.map((skc, skcIdx) => {
                        const isFirst = skcIdx === 0;
                        const remark = resolveRejectRemark(skc, item);

                        return (
                          <tr
                            key={`${item.id}-skc-${skcIdx}-${skc.skcName || 'empty'}`}
                            className={`shein-product-row shein-draft-row${isFirst ? '' : ' shein-draft-row--skc-child'}`}
                          >
                            {isFirst ? (
                              <>
                                <td
                                  rowSpan={rowSpan}
                                  className="shein-product-cell shein-product-cell--spu-check"
                                  onClick={() => toggleOne(item.id)}
                                >
                                  <input
                                    type="checkbox"
                                    aria-label={t('publishRecord.selectRecord', { title: item.title || item.id })}
                                    checked={selectedIds.has(item.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => toggleOne(item.id)}
                                  />
                                </td>
                                <td
                                  rowSpan={rowSpan}
                                  className="shein-product-cell shein-product-cell--info"
                                >
                                  <div className="shein-product-info shein-product-info--text-only shein-draft-product-info">
                                    <div className="shein-product-info__text">
                                      <HoverCopyField
                                        value={item.title || t('publishRecord.unnamed')}
                                        className="shein-product-info__title"
                                        popoverCopy
                                      />
                                      <HoverCopyField
                                        label="SPU"
                                        value={item.platformSpuName || ''}
                                        className="shein-product-info__meta"
                                        separator=": "
                                      />
                                      <HoverCopyField
                                        label={t('publishRecord.supplier')}
                                        value={item.supplierCode || ''}
                                        className="shein-product-info__meta"
                                        separator=": "
                                      />
                                      <div className="shein-product-info__meta">
                                        {t('publishRecord.category', { label: item.categoryLabel || '—' })}
                                      </div>
                                      {item.errorMessage ? (
                                        <div className="shein-product-info__meta shein-record-error" title={item.errorMessage}>
                                          {t('publishRecord.note', { message: item.errorMessage })}
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                </td>
                              </>
                            ) : null}

                            <td className="shein-product-cell shein-product-cell--spec">
                              <div className="shein-product-spec shein-draft-skc">
                                <RecordSkcThumb imageUrl={skc.imageUrl} />
                                <div className="shein-product-spec__text">
                                  <div className="shein-product-spec__color">
                                    {displaySpecLabel(skc.colorLabel) || '—'}
                                  </div>
                                  <HoverCopyField
                                    label="SKC"
                                    value={skc.skcName || ''}
                                    className="shein-product-spec__skc"
                                    separator=": "
                                  />
                                </div>
                              </div>
                            </td>

                            {isFirst ? (
                              <td
                                rowSpan={rowSpan}
                                className="shein-product-cell shein-draft-cell--type"
                              >
                                {localizedRecordLabel(t, typeLabel, i18n.language)}
                              </td>
                            ) : null}
                            <td className="shein-product-cell shein-draft-cell--status">
                              <span className={`spo-records__status spo-records__status--${resolveStatusTone(resolveSkcStatusLabel(skc, item))}`}>
                                {localizedRecordLabel(t, resolveSkcStatusLabel(skc, item), i18n.language)}
                              </span>
                            </td>
                            <td className="shein-product-cell shein-draft-cell--remark">
                              {remark ? (
                                <span className="shein-record-remark" title={remark}>{remark}</span>
                              ) : '—'}
                            </td>
                            {isFirst ? (
                              <>
                                <td
                                  rowSpan={rowSpan}
                                  className="shein-product-cell platform-product-table__muted shein-draft-cell--time"
                                >
                                  <div className="shein-record-times">
                                    <div className="shein-record-times__row">
                                      <span className="shein-record-times__label">{t('publishRecord.createdLabel')}</span>
                                      <span>{formatBeijingDateTime(createdAt)}</span>
                                    </div>
                                    <div className="shein-record-times__row">
                                      <span className="shein-record-times__label">{t('publishRecord.publishedLabel')}</span>
                                      <span>{formatBeijingDateTime(publishedAt)}</span>
                                    </div>
                                    <div className="shein-record-times__row">
                                      <span className="shein-record-times__label">{t('publishRecord.listedLabel')}</span>
                                      <span>
                                        {shelfLabel === '自动上架' || shelfLabel === '—'
                                          ? (shelfLabel === '自动上架' ? t('publishRecord.autoList') : shelfLabel)
                                          : formatBeijingDateTime(shelfLabel)}
                                      </span>
                                    </div>
                                  </div>
                                </td>
                                <td
                                  rowSpan={rowSpan}
                                  className="shein-product-cell shein-draft-cell--actions"
                                >
                                  <div className="shein-draft-actions">
                                    <button
                                      type="button"
                                      className="wf-tasks-actions__link"
                                      disabled={loading || editingId === item.id || revokingId === item.id}
                                      onClick={() => onEdit(item)}
                                    >
                                      {editingId === item.id ? t('publishRecord.checking') : t('publishRecord.edit')}
                                    </button>
                                    <button
                                      type="button"
                                      className="wf-tasks-actions__link"
                                      disabled={loading || copyingId === item.id || editingId === item.id || revokingId === item.id}
                                      onClick={() => onCopyToDraft(item)}
                                    >
                                      {copyingId === item.id ? t('publishRecord.copying') : t('publishRecord.copy')}
                                    </button>
                                    {canRevokeRecord(item) ? (
                                      <button
                                        type="button"
                                        className="wf-tasks-actions__link shein-record-revoke"
                                        disabled={loading || revokingId === item.id}
                                        onClick={() => onRevoke(item)}
                                      >
                                        {revokingId === item.id ? t('publishRecord.withdrawing') : t('publishRecord.withdraw')}
                                      </button>
                                    ) : null}
                                  </div>
                                </td>
                              </>
                            ) : null}
                          </tr>
                        );
                      });
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <SheinOpsPager
            page={page}
            pageSize={pageSize}
            total={total}
            loading={loading}
            disabled={!shopId}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            ariaLabel={t('publishRecord.pager')}
          />
        </div>
      </ProductListCard>

      <BatchQueryModal
        open={Boolean(batchModalField)}
        title={batchModalTitle}
        initialValue={batchModalField ? filterDraft[batchModalField] : ''}
        placeholder={batchModalMeta?.placeholder ? t(batchModalMeta.placeholder) : ''}
        onConfirm={onBatchConfirm}
        onClose={() => setBatchModalField(null)}
      />
    </>
  );
}
